import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Shield, Target, Mail, Globe, Folder, Server,
  BookOpen, Trophy, User, Home, LogOut, ArrowLeft, Lock, Menu, X,
} from 'lucide-react';

const ChallengeHub = ({ apiService, userProfile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const [filteredChallenges, setFilteredChallenges] = useState([]);
  const [currentArena, setCurrentArena] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showSimulationModal, setShowSimulationModal] = useState(false);
  const [arenaCompleted, setArenaCompleted] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState(null);
  const [showMobilePanel, setShowMobilePanel] = useState(false);
  const queryParams = new URLSearchParams(location.search);
  const arenaId = queryParams.get('arena');
  const topicFilter = queryParams.get('topic');

  // 1. Refs for video elements
  const beginnerVideoRef = useRef(null);
  const intermediateVideoRef = useRef(null);
  const advanceVideoRef = useRef(null);

  // 2. Helper function to ensure proper video play logic
  const handleVideoPlay = (ref) => {
    if (ref.current) {
      // Always reset and attempt to play
      ref.current.pause();
      ref.current.currentTime = 0;

      // Attempt to play and handle the browser's autoplay promise rejection
      ref.current.play().catch(error => {
        // This catch block is crucial for handling silent browser policy blocks
        console.warn("Video playback blocked by browser autoplay policy:", error);
      });
    }
  };


  const handleStartChallenge = (challenge) => {
    if (challenge.isLocked) {
      console.log("Challenge is locked:", challenge.lockReason);
      alert(challenge.lockReason || "This challenge is locked. Complete previous challenges first.");
      return;
    }
    if (challenge.completed) {
      console.log("Challenge already completed, cannot restart");
      return;
    }
    if (challenge && challenge.id) {
      const arenaParams = arenaId ? `?arena=${arenaId}${topicFilter ? `&topic=${topicFilter}` : ''}` : '';
      navigate(`/challenge/${challenge.id}${arenaParams}`, { state: { imageUrl: challenge.imageUrl } });
    } else {
      console.error("Invalid challenge object or ID.");
    }
  };

  const handleLearningNavigation = () => {
    const arenaToLearningMap = {
      'phishing_social_engineering': 'phishing_social_engineering',
      'password_security': 'password_security',
      'malware_ransomware': 'malware_ransomware',
      'network_security': 'network_security',
      'data_protection': 'data_protection',
      'incident_response': 'incident_response'
    };
    const learningModuleId = arenaToLearningMap[arenaId] || 'phishing_social_engineering';
    navigate(`/learning/module/${learningModuleId}`);
  };

  const handleViewChapter = () => {
    if (arenaId) {
      navigate(`/dashboard?tutorial=${arenaId}&step=0`);
    }
  };

  const arenaData = {
    'phishing_social_engineering': { title: 'Phishing & Social Engineering Arena' },
    'password_security': { title: 'Password Security & Authentication Arena' },
    'malware_ransomware': { title: 'Malware & Ransomware Arena' },
    'network_security': { title: 'Network Security Arena' },
    'data_protection': { title: 'Data Protection Arena' },
    'incident_response': { title: 'Incident Response Arena' }
  };

  const sortAndLockChallenges = (challenges) => {
    if (!Array.isArray(challenges)) return [];
    const sortedChallenges = [...challenges].sort((a, b) => {
      return (a.order || a.id) - (b.order || b.id);
    });
    return sortedChallenges.map((challenge, index) => {
      if (index === 0) {
        return { ...challenge, isLocked: false, lockReason: null };
      }
      const previousChallenge = sortedChallenges[index - 1];
      const isLocked = !previousChallenge.completed;
      return {
        ...challenge,
        isLocked: isLocked,
        lockReason: isLocked ? `Complete "${previousChallenge.title}" first` : null,
      };
    });
  };

  useEffect(() => {
    const fetchArenaChallenges = async () => {
      setIsLoading(true);

      // Check subscription plan - free users can only access demo arena
      // Try multiple paths to get plan ID (from currentUser or userProfile)
      const userPlanId = currentUser?.currentPlanId ||
        currentUser?.current_plan_id ||
        userProfile?.currentPlanId ||
        userProfile?.current_plan_id ||
        userProfile?.data?.current_plan_id;

      if (userPlanId === 'free') {
        alert('Your current plan (Free) only allows access to the Demo Arena. Please upgrade your subscription to access full arenas.');
        navigate('/demo-arena');
        setIsLoading(false);
        return;
      }

      // Check if user has subscription features for this arena (if arenaId is provided)
      if (arenaId && arenaData[arenaId]) {
        const subscriptionFeatures = currentUser?.subscription?.features ||
          userProfile?.subscription?.features ||
          userProfile?.data?.subscription?.features;

        if (subscriptionFeatures && subscriptionFeatures.subscription_levels) {
          const allowedArenas = Object.keys(subscriptionFeatures.subscription_levels);
          // Map arena IDs to subscription_levels keys
          const arenaMapping = {
            'phishing_social_engineering': 'phishing_social_engineering',
            'password_security': 'password_security',
            'data_protection': 'data_protection',
            'network_security': 'network_security',
            'incident_response': 'incident_response',
            'malware_ransomware': 'malware_ransomware'
          };

          const mappedArenaId = arenaMapping[arenaId];
          if (mappedArenaId && !allowedArenas.includes(mappedArenaId)) {
            alert(`This arena is not included in your current subscription plan. Please upgrade to access this arena.`);
            navigate('/dashboard');
            setIsLoading(false);
            return;
          }
        }

        setCurrentArena(arenaData[arenaId]);
        try {
          const topics = topicFilter ? topicFilter.split(',') : [];
          let challenges = [];
          if (apiService) {
            // Fetch 10 challenges per difficulty level (1, 2, 3) for the arena
            const apiParams = {
              limit: '10',  // 10 challenges per difficulty level
              perDifficulty: 'true'  // Enable per-difficulty fetching
            };
            if (arenaId) apiParams.arena = arenaId;  // Use arena instead of category to support associated_arena
            if (topics.length > 0) apiParams.topics = topics.join(',');
            const queryString = new URLSearchParams(apiParams).toString();
            const response = await apiService.get(`/challenges?${queryString}`);
            if (response.success && response.data && response.data.length > 0) {
              challenges = response.data;
              console.log(`✅ Fetched ${challenges.length} challenges (10 per difficulty) from backend`);
              if (response.groupedByDifficulty) {
                // console.log('📊 Challenges grouped by difficulty:', {
                //   beginner: response.groupedByDifficulty[1]?.length || 0,
                //   intermediate: response.groupedByDifficulty[2]?.length || 0,
                //   advanced: response.groupedByDifficulty[3]?.length || 0
                // });
              }
            }
          }
          const transformedChallenges = challenges.map((challenge) => ({
            ...challenge,
            difficulty: challenge.difficulty ? String(challenge.difficulty) : 'Beginner',
            estimatedTime: challenge.estimated_time || challenge.estimatedTime || '5-10 min',
            xpReward: challenge.points || 0,
            imageUrl: challenge.imageUrl || '/assets/images/challenge-placeholder.jpg'
          }));
          setFilteredChallenges(sortAndLockChallenges(transformedChallenges));
          setArenaCompleted(false);
        } catch (err) {
          console.error("Failed to fetch arena challenges:", err);
          setFilteredChallenges([]);
        }
      } else {
        setCurrentArena(null);
        setFilteredChallenges([]);
      }
      setIsLoading(false);
    };
    fetchArenaChallenges();
  }, [arenaId, topicFilter, apiService, currentUser, userProfile, navigate]);

  useEffect(() => {
    const extractEmail = (obj) => (
      obj?.email ||
      obj?.user?.email ||
      obj?.data?.email ||
      obj?.data?.user?.email ||
      obj?.profile?.email ||
      obj?.data?.profile?.email ||
      null
    );
    const loadEmail = async () => {
      if (currentUser?.email) {
        setUserEmail(currentUser.email);
        return;
      }
      const candidateKeys = ['currentUser', 'user', 'user_data', 'profile'];
      try {
        for (const key of candidateKeys) {
          const raw = localStorage.getItem(key) || sessionStorage.getItem(key);
          if (raw) {
            try {
              const obj = JSON.parse(raw);
              const email = obj?.email || obj?.user?.email || obj?.user_data?.email;
              if (email) {
                setUserEmail(email);
                return;
              }
            } catch (_) { }
          }
        }
      } catch (_) { }
      if (userProfile?.email) {
        setUserEmail(userProfile.email);
        return;
      }
      try {
        if (apiService) {
          const me = await apiService.get('/users/me');
          const emailMe = extractEmail(me);
          if (emailMe) { setUserEmail(emailMe); return; }
          const up = await apiService.get('/users/profile').catch(() => null);
          const emailUp = extractEmail(up);
          if (emailUp) { setUserEmail(emailUp); return; }
          const p = await apiService.get('/profile').catch(() => null);
          const emailP = extractEmail(p);
          if (emailP) { setUserEmail(emailP); return; }
        }
      } catch (_) { }
    };
    loadEmail();
  }, [currentUser, userProfile, apiService]);

  const handleInitiateSimulation = async () => {
    try {
      const email = (userEmail || '').trim();
      if (!email) {
        alert('Please enter an email to send the simulation.');
        return;
      }
      const body = { email, subject: 'Urgent Security Alert' };
      const response = await apiService.post('/phishing/send', body);
      if (response?.success) {
        alert('Live Simulation initiated. Check your email.');
      } else {
        alert('Failed to initiate simulation.');
      }
    } catch (e) {
      console.error('Initiate simulation failed:', e);
      alert('Error initiating simulation.');
    } finally {
      setShowSimulationModal(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen bg-black">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-yellow-400"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row min-h-screen bg-black text-white font-sans">
      {/* Left Banner */}
      <aside className="hidden lg:flex w-80 bg-gradient-to-b from-[#4a3625] to-black p-6 flex-col">
        <div className="flex-1">
          <h1 className="text-3xl font-extrabold mb-4" style={{ textShadow: '0 0 5px rgba(13, 13, 13, 1), 0 0 10px rgba(255, 72, 0, 1), 0 0 20px rgba(255, 213, 0, 1)' }}>
            {currentArena?.title || "Challenge Arena"}
          </h1>
          <img
            src="/assets/images//characters/Shanaya1.png"
            alt="Arena Guide"
            className="w-full h-[500px] object-cover rounded-xl shadow-lg mb-4"
          />
        </div>
        <div className="flex gap-6 justify-evenly">
          <button
            onClick={handleLearningNavigation}
            className="w-20 h-20 rounded-full bg-purple-600/80 backdrop-blur-sm shadow-[0_0_5px_#0ff,0_0_15px_#0ff,0_0_25px_#0ff] border border-purple-400/50 flex items-center justify-center text-white font-bold text-sm hover:bg-purple-600 hover:shadow-[0_0_10px_#ff0,0_0_10px_#ff0,0_0_10px_#ff0] hover:scale-105 gap-2 transition-all duration-300">
            <BookOpen size={20} />
            LEARN
          </button>
          {arenaId && (
            <button
              onClick={handleViewChapter}
              className="w-20 h-20 rounded-full bg-blue-600/80 backdrop-blur-sm shadow-[0_0_5px_#0ff,0_0_15px_#0ff,0_0_25px_#0ff] border border-blue-400/50 flex items-center justify-center text-white font-bold text-sm hover:bg-blue-600 hover:shadow-[0_0_10px_#ff0,0_0_10px_#ff0,0_0_10px_#ff0] hover:scale-105 transition-all duration-300"
            >
              VIEW CHAPTER
            </button>
          )}
        </div>
      </aside>

      {/* Challenge Cards */}
      <main
        className="flex-1 p-4 md:p-6 lg:p-10 overflow-y-auto relative"
        style={{
          backgroundImage: "linear-gradient(to bottom right, rgba(17, 24, 39, 0.85), rgba(0, 0, 0, 0.95)), url('/assets/images/levels_bg.gif')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="lg:hidden mb-4">
          <div className="rounded-xl border border-white/10 bg-gray-900/80 p-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold">{currentArena?.title || "Challenge Arena"}</h2>
            <button
              onClick={() => setShowMobilePanel((prev) => !prev)}
              className="p-2 rounded-lg bg-white/10 hover:bg-blue-600 transition-colors"
              aria-label="Toggle challenge tools"
            >
              {showMobilePanel ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
          {showMobilePanel && (
            <div className="mt-2 rounded-xl border border-white/10 bg-gray-900/90 p-3 space-y-3">
              <img
                src="/assets/images//characters/Shanaya1.png"
                alt="Arena Guide"
                className="w-full h-48 object-cover rounded-xl shadow-lg"
              />
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={handleLearningNavigation}
                  className="rounded-xl bg-purple-600/80 border border-purple-400/50 py-2 text-sm font-semibold hover:bg-purple-600 transition-colors"
                >
                  Learn
                </button>
                {arenaId && (
                  <button
                    onClick={handleViewChapter}
                    className="rounded-xl bg-blue-600/80 border border-blue-400/50 py-2 text-sm font-semibold hover:bg-blue-600 transition-colors"
                  >
                    View Chapter
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {showSimulationModal && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
            <div className="bg-gray-900 border border-yellow-400/40 rounded-xl p-6 w-full max-w-md shadow-[0_0_20px_rgba(251,191,36,0.3)]">
              <h3 className="text-xl font-bold mb-2">Initiate Live Simulation?</h3>
              <p className="text-gray-300 mb-6">All challenges in this arena are completed. A live phishing awareness simulation email will be sent to <span className="font-semibold text-yellow-300">{userEmail || 'your account email'}</span>.</p>
              <div className="flex gap-3 justify-end">
                <button onClick={() => setShowSimulationModal(false)} className="px-4 py-2 rounded-md bg-gray-700 hover:bg-gray-600">Cancel</button>
                <button onClick={handleInitiateSimulation} className="px-4 py-2 rounded-md bg-green-600 hover:bg-green-500">Yes, Initiate</button>
              </div>
            </div>
          </div>
        )}

        <div className="mb-6 flex items-center justify-between">
          <div className="flex flex-wrap gap-3">
            {/* Back to Dashboard — only when NO difficulty selected */}
            {!selectedDifficulty && (
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 rounded-lg p-2 text-gray-300 hover:text-white transition-colors duration-200 border border-green-500/30 shadow-[0_0_5px_#0ff,0_0_15px_#0ff,0_0_25px_#0ff] hover:shadow-[0_0_10px_#ff0,0_0_10px_#ff0,0_0_10px_#ffb]"
              >
                <ArrowLeft size={20} />
                <span>Back to Dashboard</span>
              </button>
            )}

            {/* Back to Levels — only when a difficulty IS selected */}
            {selectedDifficulty && (
              <button
                onClick={() => setSelectedDifficulty(null)}
                className="flex items-center gap-2 rounded-lg p-2 text-gray-300 hover:text-white transition-colors duration-200 border border-yellow-500/30 shadow-[0_0_5px_#fbbf24,0_0_15px_#fbbf24] hover:shadow-[0_0_10px_#f59e0b]"
              >
                <ArrowLeft size={20} />
                <span>Back to Levels</span>
              </button>
            )}
          </div>
        </div>

        {!selectedDifficulty ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-8 mt-8">
            {/* Beginner Card */}
            <div
               onClick={() => setSelectedDifficulty('Beginner')}
              onMouseEnter={() => handleVideoPlay(beginnerVideoRef)} // <--- NEW HANDLER CALL
              onMouseLeave={() => {
                if (beginnerVideoRef.current) {
                  beginnerVideoRef.current.pause();
                  beginnerVideoRef.current.currentTime = 0;
                }
              }}
              className="cursor-pointer group relative overflow-hidden rounded-xl bg-gray-800 hover:scale-105 transition-all duration-300 hover:shadow-[0_0_20px_rgba(74,222,128,0.3)] min-h-[360px] md:min-h-[520px]"
            >
              <video
                ref={beginnerVideoRef}
                src="/assets/images/difficulty-beginner.mp4"
                muted
                loop
                className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-8 flex flex-col items-center text-center">
                <h3 className="text-2xl sm:text-3xl font-bold text-blue-400 mb-2 drop-shadow-lg">The Beginner's Chapter</h3>
                <p className="text-gray-300 text-sm sm:text-lg drop-shadow-md">Start journey with fundamental challenges.</p>
              </div>
            </div>

            {/* Intermediate Card */}
            <div
              onClick={() => setSelectedDifficulty('Intermediate')}
              onMouseEnter={() => handleVideoPlay(intermediateVideoRef)} // <--- NEW HANDLER CALL
              onMouseLeave={() => {
                if (intermediateVideoRef.current) {
                  intermediateVideoRef.current.pause();
                  intermediateVideoRef.current.currentTime = 0;
                }
              }}
              className="relative min-h-[360px] md:min-h-[520px] rounded-lg overflow-hidden bg-gray-800 shadow-lg transition-all duration-300 cursor-pointer group hover:scale-105 hover:shadow-[0_0_20px_rgba(251,191,36,0.4)]"
            >
              <video
                ref={intermediateVideoRef}
                src="/assets/images/difficulty-intermediate.mp4"
                muted
                loop
                className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-8 flex flex-col items-center text-center">
                <h3 className="text-2xl sm:text-3xl font-bold text-cyan-400 mb-2 drop-shadow-lg">The Gauntlet</h3>
                <p className="text-gray-300 text-sm sm:text-lg drop-shadow-md">Test skills with more complex scenarios.</p>
              </div>
            </div>

            {/* Advance Card */}
            <div
              onClick={() => {
                const hasPlan = userProfile?.current_plan_id || currentUser?.current_plan_id;
                if (hasPlan) {
                  setSelectedDifficulty('Advance');
                }
              }}
              onMouseEnter={() => handleVideoPlay(advanceVideoRef)} // <--- NEW HANDLER CALL
              onMouseLeave={() => {
                if (advanceVideoRef.current) {
                  advanceVideoRef.current.pause();
                  advanceVideoRef.current.currentTime = 0;
                }
              }}
              className={`relative min-h-[360px] md:min-h-[520px] rounded-lg overflow-hidden bg-gray-800 shadow-lg transition-all duration-300 group hover:scale-105 hover:shadow-[0_0_20px_rgba(251,191,36,0.4)] ${(userProfile?.current_plan_id || currentUser?.current_plan_id) ? 'cursor-pointer' : 'cursor-not-allowed'
                }`}
            >
              <video
                ref={advanceVideoRef}
                src="/assets/images/difficulty-advance.mp4"
                muted
                loop
                className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />

              {/* Lock Overlay on Hover */}
              <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20">
                <Lock size={48} className="text-yellow-400 mb-2" />
                <p className="text-yellow-400 font-bold text-lg text-center px-4">Subscribe for advance pack to unlock</p>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-8 flex flex-col items-center text-center">
                <h3 className="text-2xl sm:text-3xl font-bold text-yellow-400 mb-2 drop-shadow-lg">Digital Frontier</h3>
                <p className="text-gray-300 text-sm sm:text-lg drop-shadow-md">Master level challenges for experts.</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6">
            {filteredChallenges
              .filter(challenge => {
                if (selectedDifficulty === 'Beginner') return challenge.difficulty === '1';
                if (selectedDifficulty === 'Intermediate') return challenge.difficulty === '2';
                if (selectedDifficulty === 'Advance') return challenge.difficulty === '3';
                return false;
              })
              .map((challenge, index) => (
                <div
                  key={challenge.id}
                  className={`relative rounded-lg overflow-hidden bg-gray-800 shadow-lg transition-all duration-300 ${challenge.isLocked
                    ? 'cursor-not-allowed'
                    : challenge.completed
                      ? 'cursor-not-allowed opacity-75'
                      : 'cursor-pointer group hover:scale-105 hover:shadow-[0_0_20px_rgba(251,191,36,0.4)]'
                    }`}
                  onClick={() => handleStartChallenge(challenge)}
                >
                  {challenge.isLocked && (
                    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/50 backdrop-blur-sm">
                      <Lock size={48} className="text-gray-400 mb-2" />
                      <p className="text-gray-300 text-sm text-center px-4">
                        {challenge.lockReason}
                      </p>
                    </div>
                  )}
                  {challenge.completed && !challenge.isLocked && (
                    <div className="absolute top-3 right-0 z-10 bg-yellow-500 text-black font-bold px-4 py-1 transform rotate-12 shadow-md">
                      COMPLETE
                    </div>
                  )}
                  <img
                    src={challenge.imageUrl}
                    alt={challenge.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-4 bg-black/70">
                    <h3 className="font-bold text-lg">{challenge.title}</h3>
                    <p className="text-sm text-gray-400 line-clamp-2">{challenge.description}</p>
                    <div className="mt-2 flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded ${selectedDifficulty === 'Beginner' ? 'bg-green-900/50 text-green-400' :
                        selectedDifficulty === 'Intermediate' ? 'bg-yellow-900/50 text-yellow-400' :
                          'bg-red-900/50 text-red-400'
                        }`}>
                        {selectedDifficulty}
                      </span>
                      <span className="text-xs text-yellow-400 font-bold">{challenge.points || challenge.xpReward} Pts</span>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default ChallengeHub;